import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { Address } from '@common/data-models/complex-types/address';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { KeyName } from '@src/suite/common/utils/util';

@Component({
    selector: 'st-address-form',
    templateUrl: './address-form.html',
    styleUrls: ['./address-form.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-address-form',
    },
})
export class AddressFormComponent extends EnpsControlComponent<Address> implements AfterViewInit, OnDestroy {
    autocomplete = signal<{ service: google.maps.places.AutocompleteService; places: google.maps.places.PlacesService }>(null);
    predictions = signal([]);
    locationShareUrl = signal<string>('');
    useAppleMaps = localStorage.getItem('useAppleMaps') ? localStorage.getItem('useAppleMaps') : 'FALSE';
    sessionToken = signal<google.maps.places.AutocompleteSessionToken>(null);
    internalFormControl: FormControl<string | number> = new FormControl('');
    internalValue = signal<Address>(new Address());
    isPanelOpen = signal<boolean>(false);

    internalAddress = signal<string>('');
    internalLocation = signal<string>('');
    internalCity = signal<string>('');
    internalState = signal<string>('');
    internalCountry = signal<string>('');

    formattedAddressLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebFormattedAddressLabel))();
    });

    foormattedAddressValue = computed(() => {
        return this.internalValue()?.isManualEditMode ? this.internalValue()?.formattedAddress : this.internalValue()?.title;
    });

    streetAddressPlaceholder = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebStreetAddressPlaceholder))();
    });

    locationNamePlaceholder = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebLocationNamePlaceholder))();
    });

    cityPlaceholder = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebCityPlaceholder))();
    });

    statePlaceholder = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebStatePlaceholder))();
    });

    countryPlaceholder = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebCountryPlaceholder))();
    });

    constructor() {
        super();
        effect(
            () => {
                this.internalValue.set(this.value() || new Address());
                this._syncValueIfNecessary();
            },
            { allowSignalWrites: true }
        );
    }

    ngAfterViewInit() {
        this.appService.mapService.getAutocomplete().then(autocomplete => {
            this.autocomplete.set(autocomplete);
            this.sessionToken.set(new google.maps.places.AutocompleteSessionToken());
        });
        this.locationShareUrl.set(this.createMapLink());
    }

    addressChanged() {
        this.locationShareUrl.set(this.createMapLink());
        this.changed.emit(this.value);
    }

    private createMapLink(): string {
        let _res = '';
        if (this.internalValue()?.formattedAddress) {
            if (this.useAppleMaps.toUpperCase() === 'TRUE') {
                _res = `http://maps.apple.com/maps?address=${this.internalValue().lat},${this.internalValue().long}`;
            } else if (this.internalValue().formattedAddress !== '') {
                _res = `https://maps.google.com/maps?q=${encodeURI(this.internalValue().formattedAddress)}`;
            } else {
                _res = '';
            }
        } else {
            _res = '';
        }
        return _res;
    }

    handlePlaceChanged() {
        this.locationShareUrl.set(this.createMapLink());
    }

    displayWith(item: any): any {
        return item?.description || item || '';
    }

    clearInternalFields() {
        this.internalAddress.set('');
        this.internalCity.set('');
        this.internalCountry.set('');
        this.internalLocation.set('');
        this.internalState.set('');
    }

    selectPrediction(result) {
        let predicitonAddress;
        if (result.description) {
            predicitonAddress = result.description;
        }
        this.ngZone.run(() => {
            this.autocomplete().places.getDetails(
                {
                    placeId: result.place_id,
                    sessionToken: this.sessionToken(),
                    fields: [
                        'address_component',
                        'adr_address',
                        'alt_id',
                        'formatted_address',
                        'geometry',
                        'icon',
                        'id',
                        'name',
                        'business_status',
                        'photo',
                        'place_id',
                        'plus_code',
                        'scope',
                        'type',
                        'url',
                        'utc_offset_minutes',
                        'vicinity',
                    ],
                },
                async (place, status) => {
                    this.sessionToken.set(new google.maps.places.AutocompleteSessionToken());
                    if (status === google.maps.places.PlacesServiceStatus.OK && place && place.geometry) {
                        this.internalValue.update(value => {
                            value.isManualEditMode = false;
                            value.clear();
                            this.clearInternalFields();
                            value.lat = place.geometry.location.lat();
                            value.long = place.geometry.location.lng();

                            let locationName = '';
                            value.placeId = place.place_id;
                            if (place.types.includes('point_of_interest')) {
                                locationName = place.name;
                            }
                            value.title = predicitonAddress;
                            value.formattedAddress = place.formatted_address;
                            let streetNumber: string = '';
                            let route: string = '';
                            for (let i = 0; i < place.address_components.length; i++) {
                                const addressType = place.address_components[i];
                                if (addressType.types.includes('country')) {
                                    value.country = place.address_components[i].long_name;
                                    value.shortCountry = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('administrative_area_level_1')) {
                                    value.state = place.address_components[i].long_name;
                                    value.administrativeArea = place.address_components[i].long_name;
                                    value.shortState = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('locality')) {
                                    value.locality = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('sublocality')) {
                                    value.subLocality = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('neighborhood')) {
                                    value.neighborhood = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('premise')) {
                                    value.locationName = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('street_number')) {
                                    streetNumber = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('route')) {
                                    route = place.address_components[i].short_name;
                                }
                                if (addressType.types.includes('postal_code')) {
                                    value.postalCode = place.address_components[i].short_name;
                                }
                            }
                            value.city = value.locality ? value.locality : value.subLocality ? value.subLocality : value.neighborhood ? value.neighborhood : '';
                            value.streetAddress = streetNumber ? `${streetNumber} ${route}` : route;
                            if (locationName !== value.streetAddress) {
                                value.locationName = locationName;
                            } else {
                                value.locationName = '';
                            }
                            return value;
                        });

                        await this.handlePlaceChanged();
                        this.predictions.set([result]);
                        this.changed.emit(this.internalValue());
                    }
                }
            );
        });
    }

    close() {
        if (this.isValueChanged()) {
            this.internalValue.update(value => {
                value.streetAddress = this.internalAddress() || value.streetAddress;
                value.locationName = this.internalLocation() || value.locationName;
                value.city = this.internalCity() || value.city;
                value.state = this.internalState() || value.state;
                value.country = this.internalCountry() || value.country;
                value.updateFormattedAddress();
                return value;
            });
            this.changed.emit(this.value());

            this.isValueChanged.set(false);
        }
        this.isPanelOpen.set(false);
    }

    loadPredictions(event) {
        if (this.autocomplete() && this.autocomplete()?.service && this.autocomplete()?.places && event.target.value) {
            this.predictions.set([]);
            const address = this.internalValue()?.getAddressForPlacePredictions() || '';
            if (address === '') return;
            this.autocomplete().service.getPlacePredictions({ input: address, sessionToken: this.sessionToken() }, (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    if (predictions) {
                        this.predictions.update(preds => {
                            preds = [...preds, ...predictions];
                            return preds;
                        });
                    }
                }
            });
        }
    }

    searchForPlaces(event) {
        if (this.autocomplete() && this.autocomplete().service && this.autocomplete().places && event.target.value) {
            this.autocomplete().service.getPlacePredictions({ input: event.target.value, sessionToken: this.sessionToken() }, (predictions, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    this.predictions.set(predictions);
                    this.predictions.update(val => {
                        for (const result of val) {
                            result.structured_formatting.main_strings = [];
                            let lastOffset = 0;
                            for (const matchedStr of result.structured_formatting.main_text_matched_substrings) {
                                if (matchedStr.offset > lastOffset) {
                                    result.structured_formatting.main_strings.push({
                                        text: result.structured_formatting.main_text.substr(lastOffset, matchedStr.offset - lastOffset),
                                        match: false,
                                    });
                                    lastOffset = matchedStr.offset;
                                } else {
                                    result.structured_formatting.main_strings.push({
                                        text: result.structured_formatting.main_text.substr(matchedStr.offset, matchedStr.length),
                                        match: true,
                                    });
                                    lastOffset = matchedStr.offset + matchedStr.length;
                                }
                            }
                            if (lastOffset > 0 && lastOffset < result.structured_formatting.main_text.length) {
                                result.structured_formatting.main_strings.push({
                                    text: result.structured_formatting.main_text.substr(lastOffset, result.structured_formatting.main_text.length - lastOffset),
                                    match: false,
                                });
                            } else if (lastOffset === 0) {
                                result.structured_formatting.main_strings.push({
                                    text: result.structured_formatting.main_text,
                                    match: false,
                                });
                            }
                        }
                        return val;
                    });
                }
            });
        } else if (!event.target.value || event.target.value.length === 0) {
            this.internalValue.update(value => {
                value.clear();
                return value;
            });
            this.predictions.set([]);
            this.changed.emit(this.internalValue());
        }
    }

    onFocus(event) {
        if (event.target.value) {
            this.loadPredictions(event);
        }
    }

    internalAddressChanged(event) {
        this.isValueChanged.set(true);
        this.internalAddress.set(event);
    }

    internalLocationChanged(event) {
        this.isValueChanged.set(true);
        this.internalLocation.set(event);
    }

    internalCityChanged(event) {
        this.isValueChanged.set(true);
        this.internalCity.set(event);
    }

    internalStateChanged(event) {
        this.isValueChanged.set(true);
        this.internalState.set(event);
    }

    internalCountryChanged(event) {
        this.isValueChanged.set(true);
        this.internalCountry.set(event);
    }

    _addValidatorIfNecessary() {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.markAllAsTouched();
    }

    _syncValueIfNecessary() {
        if (this?.internalValue()?.isManualEditMode) {
            this.internalFormControl.setValue(this.internalValue()?.formattedAddress);
        } else {
            this.internalFormControl.setValue(this.internalValue()?.title);
        }
    }

    _setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.internalFormControl.disable();
        } else {
            this.internalFormControl.enable();
        }
    }

    onEscClick(event: KeyboardEvent) {
        if (event.key === KeyName.Escape) {
            this.close();
        }
    }
}
